import { Component, Output, EventEmitter, Input, signal, computed, ViewEncapsulation, Signal, inject, effect } from '@angular/core';
import { CoreService } from 'src/app/shared/services/core.service';
import { MatDialog } from '@angular/material/dialog';
import { navItems } from '../sidebar/sidebar-data';
import { TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/material.module';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { AppSettings } from 'src/app/config';
import { MatDividerModule } from '@angular/material/divider';
import { IDataUser } from 'src/app/apps/auth/interfaces/ILoginEmployee';
import { StackDirectives } from 'src/app/shared/directives/stack-directives';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { Menu } from 'primeng/menu';
import { MasterService } from 'src/app/shared/services/master.service';
import { IListBox } from 'src/app/shared/interfaces/IClaveValor';
import { AuthStoreService } from 'src/app/apps/auth/services/auth-store.service';
import { AzureAuthService } from 'src/app/apps/auth/services/azure-auth.service';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { ChatwootNotificationService, InboxPendingConversation, PendingConversation } from 'src/app/shared/services/chatwoot-notification.service';
import { PreNominaMonitorService, PreNominaNotification } from 'src/app/shared/services/prenomina-monitor.service';
import { PrenominaGlobalIndicatorComponent } from 'src/app/shared/components/prenomina-global-indicator/prenomina-global-indicator.component';
import { ErrorModalComponent } from 'src/app/shared/components/error-modal/error-modal.component';
import { GuiaPanelStateService } from 'src/app/shared/services/guia-panel-state.service';

interface notifications {
  id: number;
  icon: string;
  color: string;
  title: string;
  time: string;
  subtitle: string;
}

interface profiledd {
  id: number;
  title: string;
  link: string;
  new?: boolean;
}

interface apps {
  id: number;
  icon: string;
  color: string;
  title: string;
  subtitle: string;
  link: string;
}

interface CompanyOption extends IListBox {
  searchLabel: string;
}

@Component({
  selector: 'app-header',
  imports: [RouterModule, ButtonModule, CommonModule, NgScrollbarModule, TablerIconsModule, MaterialModule, StackDirectives, Menu, PrenominaGlobalIndicatorComponent, FormsModule, SelectModule],
  templateUrl: './header.component.html',
  encapsulation: ViewEncapsulation.None,
  styles: [
    `
      .chatwoot-bell-badge {
        position: absolute;
        top: 4px;
        right: 4px;
        min-width: 16px;
        height: 16px;
        padding: 0 4px;
        border-radius: 8px;
        background: #ef4444;
        color: white;
        font-size: 10px;
        font-weight: 700;
        display: flex;
        align-items: center;
        justify-content: center;
        line-height: 1;
        animation: chatwoot-bell-pulse 1.6s infinite;
      }
      @keyframes chatwoot-bell-pulse {
        0% {
          box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.6);
        }
        70% {
          box-shadow: 0 0 0 6px rgba(239, 68, 68, 0);
        }
        100% {
          box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
        }
      }
    `
  ]
})
export class HeaderComponent {
  private masterService = inject(MasterService);
  authStore = inject(AuthStoreService);
  private router = inject(Router);
  private azureAuthService = inject(AzureAuthService);
  private loader = inject(LoaderService);
  private chatwootNotificationService = inject(ChatwootNotificationService);
  // El servicio de monitor se inyecta aquí para garantizar que el singleton
  // esté activo mientras el topbar esté montado (portal Empresas).
  readonly _preNominaMonitor = inject(PreNominaMonitorService);
  private guiaPanelState = inject(GuiaPanelStateService);

  readonly chatwootPending = this.chatwootNotificationService.pendingConversations;
  readonly chatwootTotalUnread = this.chatwootNotificationService.totalUnread;

  // ─── Bandeja global (respuestas de campaña / WhatsApp sin candidato) ─────────
  readonly chatwootInboxPending = this.chatwootNotificationService.inboxPending;
  readonly chatwootInboxUnread = this.chatwootNotificationService.inboxTotalUnread;

  /** Chat habilitado para el rol (admin-chat): gobierna la sección Mensajes y su parte del badge. */
  readonly chatHabilitado = this.chatwootNotificationService.chatAcceso.chatHabilitado;

  // ─── Notificaciones de pre-nómina ───────────────────────────────────────────
  readonly preNominaNotifs = this._preNominaMonitor.notifications;
  readonly preNominaUnread = this._preNominaMonitor.unreadCount;

  /** Total del badge de la campanita: mensajes Chatwoot (watched + bandeja global, si el rol tiene chat) + procesos de nómina. */
  readonly totalBellUnread = computed(
    () =>
      (this.chatHabilitado() ? this.chatwootTotalUnread() + this.chatwootInboxUnread() : 0) +
      this.preNominaUnread()
  );

  openChatwootConversation(c: PendingConversation): void {
    this.chatwootNotificationService.requestOpen({
      conversationId: c.conversationId,
      idCandidato: c.idCandidato,
      phone: c.phone,
      nombre: c.nombre
    });
  }

  /** Abre el drawer de chat para una conversación de la bandeja global (sin candidato). */
  openInboxConversation(c: InboxPendingConversation): void {
    this.chatwootNotificationService.openByPhone(c.phone, c.nombre);
  }

  /** Al abrir la campanita, marca las notificaciones de pre-nómina como leídas. */
  onNotificationsOpened(): void {
    this._preNominaMonitor.markNotificationsRead();
  }

  /**
   * Abre la pantalla que corresponde al aviso. La campanita mezcla pre-nómina y
   * paz y salvo: llevar siempre a Novedades y ausencias dejaba al usuario en una
   * pantalla que no tiene nada que ver con lo que acababa de leer.
   */
  openPreNominaNotif(n: PreNominaNotification): void {
    if (n?.destino === 'pazysalvo') {
      // Un paz y salvo terminado se revisa en la bandeja de pendientes; si ya se
      // cerró, el propio usuario cambia a la pestaña de cerrados.
      this.router.navigate(['/app/empresas/gestion-retiros'], {
        queryParams: { tab: 'pendientes' }
      });
      return;
    }
    this.router.navigate(['/app/empresas/nomina/novedades']);
  }

  /** Limpia el historial de notificaciones de pre-nómina. */
  clearPreNominaNotifs(): void {
    this._preNominaMonitor.clearNotifications();
  }

  @Input() showToggle = true;
  @Input() toggleChecked = false;
  @Output() toggleMobileNav = new EventEmitter<void>();
  @Output() toggleMobileFilterNav = new EventEmitter<void>();
  @Output() toggleCollapsed = new EventEmitter<void>();

  userData: Signal<IDataUser | null>;
  nameInitials: string = '';

  companies: CompanyOption[] = [];
  selectedCompany: CompanyOption | null = null;

  // Ya no mantenemos señal local; usamos la del CoreService
  // Exponer la signal para uso en template: company()
  public company: Signal<IListBox>;
  idPortal: Signal<string> = signal('');

  showFiller = false;

  public selectedLanguage: any = {
    language: 'English',
    code: 'en',
    type: 'US',
    icon: '/assets/images/flag/icon-flag-en.svg'
  };

  public languages: any[] = [
    {
      language: 'English',
      code: 'en',
      type: 'US',
      icon: '/assets/images/flag/icon-flag-en.svg'
    },
    {
      language: 'Español',
      code: 'es',
      icon: '/assets/images/flag/icon-flag-es.svg'
    },
    {
      language: 'Français',
      code: 'fr',
      icon: '/assets/images/flag/icon-flag-fr.svg'
    },
    {
      language: 'German',
      code: 'de',
      icon: '/assets/images/flag/icon-flag-de.svg'
    }
  ];

  @Output() optionsChange = new EventEmitter<AppSettings>();

  constructor(
    private settings: CoreService,
    public dialog: MatDialog,
    private translate: TranslateService
  ) {
    // translate.setDefaultLang('en');

    // obtener referencia a la señal centralizada
    this.company = this.settings.getCompanySignal();
    this.idPortal = this.settings.getIdPortalSignal();

    effect(() => {
      const currentCompany = this.company();
      if (!currentCompany?.id) {
        this.selectedCompany = null;
        return;
      }

      this.selectedCompany = {
        ...currentCompany,
        searchLabel: `${currentCompany.id} | ${currentCompany.label}`
      };
    });

    this.getDataUser();
  }

  onCompanySelected(company: CompanyOption | null): void {
    if (!company?.id) {
      return;
    }

    this.settings.setCompany({ id: company.id, label: company.label });
  }

  getDataUser() {
    const userData = this.settings.getUserData();
    if (userData) {
      this.userData = signal(userData);
      this.nameInitials = userData.nombre
        ? userData.nombre
            .split(' ')
            .map((n: string) => n[0])
            .filter((_: string, i: number) => i < 2)
            .join('')
            .toUpperCase()
        : '';
    } else {
      this.userData = signal(null);
    }

    this.masterService.getAssignedClients().subscribe({
      next: (response) => {
        if (!response || !response.data) {
          this.companies = [];
          this.selectedCompany = null;
          if (localStorage.getItem('modLog') === '2') {
            this.dialog.open(ErrorModalComponent, {
              disableClose: true,
              data: {
                statusCode: 204,
                message: 'Sin empresas asignadas',
              details: 'Tu usuario no tiene empresas asignadas. Comunícate con tu administrador.'
            }
            });
          }
          return;
        }
        const nitLS = localStorage.getItem('selectedCompany');
        if (nitLS) {
          const found = response.data.find((comp) => comp.id === nitLS.replace(/"/g, ''));
          if (found) {
            // actualizar compañía seleccionada globalmente
            this.settings.setCompany(found);
          } else {
            // Si la compañía en localStorage no está en la lista, seleccionar la primera
            const first = response.data.length > 0 ? response.data[0] : { label: '', id: '' };
            this.settings.setCompany(first);
          }
        } else {
          // Si no hay compañía en localStorage, seleccionar la primera
          const first = response.data.length > 0 ? response.data[0] : { label: '', id: '' };
          this.settings.setCompany(first);
        }

        this.companies = response.data.map((client) => ({
          ...client,
          searchLabel: `${client.id} | ${client.label}`
        }));
      },
      error: (err) => {
        this.router.navigate(['/error-page'], { queryParams: { code: 'user_config' } }); // Navegar a una página de error genérica
      }
    });
  }

  handleProfileClick(profile: profiledd) {
    if (profile.title === 'Cerrar sesión') {
      // El servicio de chat es root singleton: sobrevive a la navegación a /login.
      // Se corta aquí para que no siga polleando ni notifique al siguiente usuario.
      this.chatwootNotificationService.resetSession();
      // Limpiar el localStorage al cerrar sesión
      const loginAzure = localStorage.getItem('msal');
      if (loginAzure) {
        localStorage.clear();
        this.azureAuthService.logout().then(() => {
          this.router.navigate(['/login']);
        });
        return;
      }
      localStorage.removeItem('token');
      localStorage.removeItem('mod');
      localStorage.removeItem('userInfo');
      localStorage.removeItem('selectedCompany');
      localStorage.removeItem('portalId');
      localStorage.removeItem('userEmail');
      // Último menú abierto: si queda, el siguiente login entra a la pantalla del usuario anterior.
      localStorage.removeItem('url');
      this.router.navigate(['/login']);
    }
  }

  // Getter para usar desde código si se necesita el objeto actual
  get companySelectObj() {
    return this.settings.getCompany();
  }

  options = this.settings.getOptions();

  openDialog() {
    const dialogRef = this.dialog.open(AppSearchDialogComponent);

    dialogRef.afterClosed().subscribe((result) => {
      // console.log(`Dialog result: ${result}`);
    });
  }

  changeLanguage(lang: any): void {
    this.translate.use(lang.code);
    this.selectedLanguage = lang;
  }

  /** Abre el panel lateral derecho de la Guía de usuario. */
  abrirGuia(): void {
    this.guiaPanelState.abrir();
  }

  setlightDark(theme: string) {
    this.options.theme = theme;
    this.settings.setOptions({ theme: theme });
    if (theme === 'dark') {
      this.options.logo = '/assets/images/logos/nexus-l.png';
    } else {
      this.options.logo = '/assets/images/logos/nexus-d.png';
    }
    this.emitOptions();
  }

  private emitOptions() {
    this.optionsChange.emit(this.options);
  }

  notifications: notifications[] = [
    {
      id: 1,
      icon: 'a-b-2',
      color: 'primary',
      time: '8:30 AM',
      title: 'Launch Admin',
      subtitle: 'Just see the my new admin!'
    },
    {
      id: 2,
      icon: 'calendar',
      color: 'secondary',
      time: '8:21 AM',
      title: 'Event today',
      subtitle: 'Just a reminder that you have event'
    },
    {
      id: 3,
      icon: 'settings',
      color: 'warning',
      time: '8:05 AM',
      title: 'Settings',
      subtitle: 'You can customize this template'
    },
    {
      id: 4,
      icon: 'a-b-2',
      color: 'success',
      time: '7:30 AM',
      title: 'Launch Templates',
      subtitle: 'Just see the my new admin!'
    },
    {
      id: 5,
      icon: 'exclamation-circle',
      color: 'error',
      time: '7:03 AM',
      title: 'Event tomorrow',
      subtitle: 'Just a reminder that you have event'
    }
  ];

  profiledd: profiledd[] = [
    // {
    //   id: 1,
    //   title: 'My Profile',
    //   link: '/',
    // },
    // {
    //   id: 2,
    //   title: 'My Subscription',
    //   link: '/',
    // },
    // {
    //   id: 3,
    //   title: 'My Invoice',
    //   new: true,
    //   link: '/',
    // },
    // {
    //   id: 4,
    //   title: ' Account Settings',
    //   link: '/',
    // },
    {
      id: 5,
      title: 'Cerrar sesión',
      link: ''
    }
  ];

  apps: apps[] = [
    // {
    //   id: 1,
    //   icon: 'solar:chat-line-line-duotone',
    //   color: 'primary',
    //   title: 'Chat Application',
    //   subtitle: 'Messages & Emails',
    //   link: '/'
    // },
    // {
    //   id: 2,
    //   icon: 'solar:checklist-minimalistic-line-duotone',
    //   color: 'secondary',
    //   title: 'Todo App',
    //   subtitle: 'Completed task',
    //   link: '/'
    // },
    // {
    //   id: 3,
    //   icon: 'solar:bill-list-line-duotone',
    //   color: 'success',
    //   title: 'Invoice App',
    //   subtitle: 'Get latest invoice',
    //   link: '/'
    // },
    // {
    //   id: 4,
    //   icon: 'solar:calendar-line-duotone',
    //   color: 'error',
    //   title: 'Calendar App',
    //   subtitle: 'Get Dates',
    //   link: '/'
    // },
    // {
    //   id: 5,
    //   icon: 'solar:smartphone-2-line-duotone',
    //   color: 'warning',
    //   title: 'Contact Application',
    //   subtitle: '2 Unsaved Contacts',
    //   link: '/'
    // },
    // {
    //   id: 6,
    //   icon: 'solar:ticket-line-duotone',
    //   color: 'primary',
    //   title: 'Tickets App',
    //   subtitle: 'Create new ticket',
    //   link: '/'
    // },
    // {
    //   id: 7,
    //   icon: 'solar:letter-line-duotone',
    //   color: 'secondary',
    //   title: 'Email App',
    //   subtitle: 'Get new emails',
    //   link: '/'
    // },
    // {
    //   id: 8,
    //   icon: 'solar:book-2-line-duotone',
    //   color: 'warning',
    //   title: 'Contact List',
    //   subtitle: 'Create new contact',
    //   link: '/'
    // }
  ];
}

@Component({
  selector: 'search-dialog',
  imports: [RouterModule, MaterialModule, TablerIconsModule, FormsModule, MatDividerModule],
  templateUrl: 'search-dialog.component.html'
})
export class AppSearchDialogComponent {
  searchText: string = '';
  navItems = navItems;

  navItemsData = navItems.filter((navitem) => navitem.displayName);

  // filtered = this.navItemsData.find((obj) => {
  //   return obj.displayName == this.searchinput;
  // });
}
